import React, { useState } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';

// Add/Edit employee (ntrg_staff directory row) — single modal, AddConsultantModal isEdit pattern.
// FIELD-SCOPED: writes only name/email/title/role/phone/active/can_view_activity/can_close_questions — never auth_user_id
// or is_feedback_curator. can_view_activity: may read Utilities → Activity (the activity_log read rule checks it);
// can_close_questions: may close / reopen Open Questions (question_close / _reopen check it). Only admins can save a
// staff row — ntrg_staff's write policy. Add creates a directory row (no login; auth provisioning stays separate).
// Props: isDark; employee (null=add / row=edit); onClose; onSuccess(row).
export default function AddEmployeeModal({ isDark, employee, allStaff, onClose, onSuccess }) {
  const isEdit = !!employee;
  const [form, setForm] = useState({
    name:   employee?.name || '',
    email:  employee?.email || '',
    title:  employee?.title || '',
    role:   employee?.role || '',
    phone:  employee?.phone || '',
    reports_to: employee?.reports_to || '',
    active: employee ? employee.active !== false : true,
    can_view_activity: !!employee?.can_view_activity,
    can_close_questions: !!employee?.can_close_questions,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const c = isDark
    ? { card: '#2F3854', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8' };
  const s = {
    label:    { display: 'block', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' },
    input:    { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btn:      { padding: '9px 18px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '9px 16px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
  };

  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }));
  const txt = v => (v && v.trim() ? v.trim() : null);

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const fields = { name: form.name.trim(), email: txt(form.email), title: txt(form.title), role: txt(form.role), phone: txt(form.phone), reports_to: form.reports_to || null, active: !!form.active, can_view_activity: !!form.can_view_activity, can_close_questions: !!form.can_close_questions };
      const q = isEdit
        ? supabase.from('ntrg_staff').update(fields).eq('id', employee.id).select().single()
        : supabase.from('ntrg_staff').insert(fields).select().single();
      const { data, error: e } = await q;
      if (e) throw e;
      onSuccess(data);
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div {...backdropProps(onClose)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: c.card, borderRadius: '14px', padding: '28px', maxWidth: '520px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>{isEdit ? 'Edit Employee' : 'Add Employee'}</div>
          <button onClick={onClose} type="button" style={{ background: 'none', border: 'none', color: c.muted, fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>Name *</label><input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Title</label><input style={s.input} value={form.title} onChange={e => set('title', e.target.value)} /></div>
          <div><label style={s.label}>Role</label><input style={s.input} value={form.role} onChange={e => set('role', e.target.value)} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Email</label><input style={s.input} value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div><label style={s.label}>Phone</label><input style={s.input} value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={s.label}>Reports To</label>
          <select style={s.input} value={form.reports_to} onChange={e => set('reports_to', e.target.value)}>
            <option value="">— No manager (top) —</option>
            {(allStaff || []).filter(p => p.active && p.id !== employee?.id).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map(p => <option key={p.id} value={p.id}>{p.name}{p.title ? ` — ${p.title}` : ''}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: c.text, cursor: 'pointer', marginBottom: '14px' }}>
          <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} style={{ cursor: 'pointer' }} />
          Active
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: c.text, cursor: 'pointer', marginBottom: '14px' }}>
          <input type="checkbox" checked={form.can_view_activity} onChange={e => set('can_view_activity', e.target.checked)} style={{ cursor: 'pointer' }} />
          Can view Activity
          <span style={{ fontSize: '11px', color: c.muted }}>— who did what, in Utilities → Activity</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: c.text, cursor: 'pointer', marginBottom: '14px' }}>
          <input type="checkbox" checked={form.can_close_questions} onChange={e => set('can_close_questions', e.target.checked)} style={{ cursor: 'pointer' }} />
          Can close questions
          <span style={{ fontSize: '11px', color: c.muted }}>— close and reopen in Utilities → Open Questions</span>
        </label>
        {error && <div style={{ color: '#f87171', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} style={s.btn}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Employee')}</button>
        </div>
      </div>
    </div>
  );
}
